import { Directive, EventEmitter, HostListener, Output, OnDestroy, ElementRef, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { buffer, debounceTime, filter, map } from 'rxjs/operators';

/**
 * Directive to detect single and double click events on an element marked with 'data-double-click'.
 */
@Directive({
  selector: '[data-double-click]'
})
export class DoubleClickDirective implements OnDestroy {
  private readonly click$ = new Subject<MouseEvent>();
  private readonly elementRef = inject(ElementRef);

  @Output() singleClick = new EventEmitter<MouseEvent>();
  @Output() doubleClick = new EventEmitter<MouseEvent>();

  /**
   * Creates an instance of DoubleClickDirective.
   * @param elementRef Reference to the element where the directive is applied.
   */
  constructor() {
    const debouncedClicks = this.click$.pipe(debounceTime(250));

    this.click$
      .pipe(
        buffer(debouncedClicks),
        filter(clicks => clicks.length === 1 || clicks.length === 2),
        map(clicks => ({
          action: clicks.length === 1 ? 'single' : 'double',
          event: clicks[clicks.length - 1]
        }))
      )
      .subscribe(({ action, event }) => {
        if (action === 'single') {
          this.singleClick.emit(event);
        } 
        
        if (action === 'double') {
          this.doubleClick.emit(event);
        }
      });
  }

  /**
   * Handles click events on the host element.
   * Emits singleClick or doubleClick events based on the number of clicks within the debounce time.
   * @param event The mouse event triggered by a click.
   */
  @HostListener('click', ['$event'])
  onClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const hostElement = this.elementRef.nativeElement as HTMLElement;

    if (hostElement.hasAttribute('data-double-click') && hostElement.contains(target)) {
      event.stopPropagation();
      event.preventDefault();
      this.click$.next(event);
    } 
  }

  /**
   * Cleans up resources when the directive is destroyed.
   */
  ngOnDestroy() {
    this.click$.complete();
  }
}